import React, { useCallback, useMemo, useState } from "react";
import "./Summary.css";
import { useTranslation } from "react-i18next";
import WizardNav from "../../components/Wizard/WizardNav";
import { Spinner } from "../../components/Filtering/Reusable/Spinner";
import { extractIdFromIri } from "../../utils/extractFromIri";
import { formatDate } from "../../utils/constants";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import { useGetParticipantByParticipantId } from "../../hooks/participants/useGetParticipantByParticipantId";
import { useGetDistributionsByIds } from "../../hooks/distributions/useGetDistributionsByIds";
import { getParticipantDisplayName } from "../../utils/participants";
import { getTextByLang } from "../../utils/formUtils";

const dash = (v) =>
  v === null || v === undefined || String(v).trim() === "" ? "—" : v;
const firstLangValue = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  return arr[0]?.value ?? "";
};
const iriToDisplay = (value) => {
  if (Array.isArray(value)) return iriToDisplay(value[0]);
  if (value === null || value === undefined) return "";
  const normalized = String(value).trim();
  if (!normalized) return "";
  return extractIdFromIri(normalized) || normalized;
};
const toLookupId = (value) => {
  if (Array.isArray(value)) return toLookupId(value[0]);
  if (value === null || value === undefined) return "";
  const normalized = String(value).trim();
  if (!normalized) return "";
  return extractIdFromIri(normalized) || normalized;
};
const localizedValue = (value, lang = "es") => {
  const text = getTextByLang(value, lang);
  return text === "Not specified." ? "" : text;
};
const formatIsoCalendarDate = (value, locale = "es-ES") => {
  if (!value) return "";

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return formatDate(value, locale);
  }

  const [, year, month, day] = match;
  const utcDate = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), 12),
  );

  if (Number.isNaN(utcDate.getTime())) {
    return formatDate(value, locale);
  }

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(utcDate);
};
const getPolicyRuleCount = (policy, bucket) => {
  if (typeof policy?.ruleCounts?.[bucket] === "number") {
    return policy.ruleCounts[bucket];
  }

  return Array.isArray(policy?.[bucket]) ? policy[bucket].length : 0;
};
const findPolicyConstraintValue = (policy, token) => {
  const rules = [
    ...(Array.isArray(policy?.permissions) ? policy.permissions : []),
    ...(Array.isArray(policy?.obligations) ? policy.obligations : []),
    ...(Array.isArray(policy?.prohibitions) ? policy.prohibitions : []),
  ];
  const normalizedToken = String(token || "").trim().toLowerCase();

  for (const rule of rules) {
    for (const constraint of Array.isArray(rule?.constraints)
      ? rule.constraints
      : []) {
      const leftOperand = String(constraint?.left_operand || "").toLowerCase();
      if (leftOperand.includes(normalizedToken)) {
        return constraint?.right_operand || "";
      }
    }
  }

  return "";
};

const statusBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("public")) return "ss-badge ss-badge--ok";
  if (s.includes("pend")) return "ss-badge ss-badge--warn";
  if (s.includes("rech") || s.includes("fail")) return "ss-badge ss-badge--bad";
  return "ss-badge";
};

export default function Summary({
  allData,
  back,
  next,
  isFirst,
  canGoNext,
  onPublish,
  reset,
  isLast,
  isPosting = false,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t, i18n } = useTranslation();
  const { callApiWithWorker } = useWorkerApiHelper();
  const summary = useMemo(() => {
    const step1 = allData?.step1 ?? allData?.basicData ?? {};
    const step2 = allData?.step2 ?? {};
    const step3 = allData?.step3 ?? {};

    const dataset =
      (Array.isArray(step3?.selectedDatasets) && step3.selectedDatasets[0]) ||
      step3?.selectedDataset ||
      {};
    const offeringName =
      step1?.name ||
      firstLangValue(step1?.title) ||
      firstLangValue(step1?.name);

    const offeringDesc =
      step1?.descriptionText ||
      firstLangValue(step1?.description) ||
      (typeof step1?.description === "string" ? step1.description : "");
    const datasetName =
      dataset?.titleText ||
      firstLangValue(dataset?.title) ||
      dataset?.identifier ||
      dataset?.rowId ||
      dataset?.name;

    const themeRaw =
      Array.isArray(dataset?.theme) && dataset.theme.length > 0
        ? dataset.theme[0]
        : dataset?.themeLabel || dataset?.theme || "";
    const theme = iriToDisplay(themeRaw);

    const status = dataset?.statusLabel || dataset?.status || "Publicado";

    const participantIdRaw =
      dataset?.publisher || dataset?.creator || step2?.publisher || step1?.publisher;
    const authorRaw =
      dataset?.publisherLabel ||
      dataset?.creatorLabel ||
      participantIdRaw;
    const author = iriToDisplay(authorRaw);

    const modifiedRaw =
      dataset?.modifiedLabel ||
      dataset?.modified ||
      dataset?.updated_at ||
      dataset?.created_at;
    const modified =
      formatDate(modifiedRaw, i18n?.language || "es-ES") || modifiedRaw || "";

    const distributionIds = (
      Array.isArray(dataset?.distributions) && dataset.distributions.length > 0
        ? dataset.distributions
        : [dataset?.distribution?.id || dataset?.selectedDistributionId]
    )
      .map(toLookupId)
      .filter(Boolean);
    const distributionNameRaw =
      distributionIds[0] ||
      dataset?.distributionName ||
      dataset?.selectedDistributionName ||
      firstLangValue(dataset?.distribution?.title) ||
      dataset?.distribution?.title;
    const distributionName = iriToDisplay(distributionNameRaw);

    const firstPolicy = Array.isArray(step2?.selectedPolicies)
      ? step2.selectedPolicies[0]
      : null;

    const policyName =
      localizedValue(firstPolicy?.title, i18n?.language || "es") ||
      step2?.selectedPolicyName ||
      step2?.policyName ||
      (Array.isArray(step2?.configById) && step2.configById.length > 0
        ? step2.configById[0]
        : "");
    const policyDescription =
      localizedValue(firstPolicy?.description, i18n?.language || "es") || "";
    const policyCounts = {
      permissions: getPolicyRuleCount(firstPolicy, "permissions"),
      obligations: getPolicyRuleCount(firstPolicy, "obligations"),
      prohibitions: getPolicyRuleCount(firstPolicy, "prohibitions"),
    };
    const policyRecipient = findPolicyConstraintValue(firstPolicy, "recipient");
    const policyValidUntilRaw = findPolicyConstraintValue(firstPolicy, "datetime");
    const policyValidUntil =
      formatIsoCalendarDate(policyValidUntilRaw, i18n?.language || "es-ES") ||
      policyValidUntilRaw ||
      "";

    return {
      offeringName,
      offeringDesc,
      datasetName,
      theme,
      status,
      author,
      participantId: toLookupId(participantIdRaw),
      modified,
      distributionName,
      distributionIds,
      policyName,
      policyDescription,
      policyCounts,
      policyRecipient,
      policyRecipientId: toLookupId(policyRecipient),
      policyValidUntil,
    };
  }, [allData, i18n?.language]);

  const { data: publisherParticipant } = useGetParticipantByParticipantId({
    callApiWithWorker,
    participantId: summary.participantId,
    enabled: Boolean(summary.participantId),
  });

  const { data: distributionsByIds } = useGetDistributionsByIds({
    callApiWithWorker,
    ids: summary.distributionIds,
    language: i18n?.language,
    enabled: summary.distributionIds.length > 0,
  });

  const { data: policyRecipientParticipant } = useGetParticipantByParticipantId({
    callApiWithWorker,
    participantId: summary.policyRecipientId,
    enabled: Boolean(summary.policyRecipientId),
  });

  const authorLabel = useMemo(
    () =>
      getParticipantDisplayName(publisherParticipant, i18n?.language) ||
      summary.author,
    [publisherParticipant, summary.author, i18n?.language],
  );

  const distributionLabel = useMemo(() => {
    const firstDistribution = Array.isArray(distributionsByIds)
      ? distributionsByIds[0]
      : null;
    const localizedTitle = getTextByLang(
      firstDistribution?.title,
      i18n?.language || "es",
    );

    return (
      (localizedTitle !== "Not specified." ? localizedTitle : "") ||
      summary.distributionName
    );
  }, [distributionsByIds, summary.distributionName, i18n?.language]);

  const policyRecipientLabel = useMemo(
    () =>
      getParticipantDisplayName(policyRecipientParticipant, i18n?.language) ||
      summary.policyRecipient,
    [policyRecipientParticipant, summary.policyRecipient, i18n?.language],
  );

  const policyRuleSummary = useMemo(() => {
    const counts = summary.policyCounts;
    if (!counts) return "";
    if (
      !summary.policyName &&
      !summary.policyDescription &&
      counts.permissions === 0 &&
      counts.obligations === 0 &&
      counts.prohibitions === 0
    ) {
      return "";
    }

    return [
      `${counts.permissions} ${t(
        "wizard.steps.policy.table.permissions",
        "Permissions",
      )}`,
      `${counts.obligations} ${t(
        "wizard.steps.policy.table.obligations",
        "Obligations",
      )}`,
      `${counts.prohibitions} ${t(
        "wizard.steps.policy.table.prohibitions",
        "Prohibitions",
      )}`,
    ].join(" · ");
  }, [summary.policyCounts, summary.policyDescription, summary.policyName, t]);

  const policyHighlights = useMemo(
    () =>
      [
        summary.policyRecipient
          ? {
              label: t("wizard.steps.summary.policyRecipient", "Destinatario"),
              value: policyRecipientLabel,
            }
          : null,
        summary.policyValidUntil
          ? {
              label: t("wizard.steps.summary.policyValidUntil", "Válida hasta"),
              value: summary.policyValidUntil,
            }
          : null,
      ].filter(Boolean),
    [policyRecipientLabel, summary.policyRecipient, summary.policyValidUntil, t],
  );

  const handlePublish = useCallback(async () => {
    if (!canGoNext || isSubmitting || isPosting) return;

    setIsSubmitting(true);
    try {
      await onPublish?.(allData);

      reset?.();
    } finally {
      setIsSubmitting(false);
    }
  }, [allData, canGoNext, isSubmitting, isPosting, onPublish, reset]);

  return (
    <section className="ss-root">
      {(isSubmitting || isPosting) && (
        <div className="ss-loading-overlay" aria-live="polite" aria-busy="true">
          <Spinner size="large" color="var(--main-color)" />
        </div>
      )}
      <h3 className="ss-title">
        {t
          ? t("wizard.steps.summary.check", "Revise los campos rellenados")
          : "Revise los campos rellenados"}
      </h3>

      <div className="ss-card">
        {/* Column 1 */}
        <div className="ss-col">
          <div className="ss-block">
            <div className="ss-label">
              {t
                ? t("wizard.steps.offer_name", "Nombre de la oferta de datos")
                : "Nombre de la oferta de datos"}
            </div>
            <div className="ss-value">{dash(summary.offeringName)}</div>
          </div>

          <div className="ss-block ss-mt">
            <div className="ss-subLabel">
              {t
                ? t(
                    "wizard.steps.offer_description",
                    "Descripción de la oferta de datos",
                  )
                : "Descripción de la oferta de datos"}
            </div>
            <div className="ss-value ss-prewrap">
              {dash(summary.offeringDesc)}
            </div>
          </div>
        </div>

        <div className="ss-divider" />

        {/* Column 2 */}
        <div className="ss-col">
          <div className="ss-block">
            <div className="ss-label">
              {t
                ? t(
                    "wizard.steps.dataset.name_label",
                    "Nombre del producto de datos",
                  )
                : "Nombre del producto de datos"}
            </div>
            <div className="ss-value">{dash(summary.datasetName)}</div>
          </div>

          <div className="ss-row ss-mt">
            <div className="ss-half">
              <div className="ss-subLabel">
                {t ? t("wizard.steps.summary.theme", "Tema") : "Tema"}
              </div>
              <div className="ss-value">{dash(summary.theme)}</div>
            </div>

            <div className="ss-half">
              <div className="ss-subLabel">
                {t ? t("wizard.steps.summary.status", "Estado") : "Estado"}
              </div>
              <div className={statusBadgeClass(summary.status)}>
                {dash(summary.status)}
              </div>
            </div>
          </div>

          <div className="ss-row ss-mt">
            <div className="ss-half">
              <div className="ss-subLabel">
                {t ? t("wizard.steps.dataset.author", "Autor") : "Autor"}
              </div>
              <div className="ss-value">{dash(authorLabel)}</div>
            </div>

            <div className="ss-half">
              <div className="ss-subLabel">
                {t
                  ? t("wizard.steps.dataset.date", "F.Modificación")
                  : "F.Modificación"}
              </div>
              <div className="ss-value">{dash(summary.modified)}</div>
            </div>
          </div>

          <div className="ss-block ss-mt">
            <div className="ss-subLabel">
              {t
                ? t(
                    "wizard.steps.dataset.distribution.name",
                    "Nombre de la distribución",
                  )
                : "Nombre de la distribución"}
            </div>
            <div className="ss-value">{dash(distributionLabel)}</div>
          </div>
        </div>

        <div className="ss-divider" />

        {/* Column 3 */}
        <div className="ss-col">
          <div className="ss-block">
            <div className="ss-label">
              {t
                ? t("wizard.steps.policy.name", "Nombre de la política")
                : "Nombre de la política"}
            </div>
            <div className="ss-value">{dash(summary.policyName)}</div>
          </div>

          <div className="ss-block ss-mt">
            <div className="ss-subLabel">
              {t
                ? t("wizard.steps.policy.description", "Descripción")
                : "Descripción"}
            </div>
            <div className="ss-value ss-prewrap">
              {dash(summary.policyDescription)}
            </div>
          </div>

          <div className="ss-block ss-mt">
            <div className="ss-subLabel">
              {t
                ? t("wizard.steps.policy.rule", "Reglas")
                : "Reglas"}
            </div>
            <div className="ss-value">{dash(policyRuleSummary)}</div>
          </div>

          {policyHighlights.map((item) => (
            <div key={item.label} className="ss-block ss-mt">
              <div className="ss-subLabel">{item.label}</div>
              <div className="ss-value">{dash(item.value)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="wz-footer">
        <div className="wz-footer">
          <WizardNav
            back={back}
            next={next}
            canGoNext={canGoNext && !isSubmitting && !isPosting}
            isFirst={isFirst}
            isLast={isLast}
            onSubmit={handlePublish}
            isSubmitting={isSubmitting || isPosting}
          />
        </div>
      </div>
    </section>
  );
}
